import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { DollarSign, ArrowLeft, Plus, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/libritos';

export default function AdminLibritosVentasPage() {
  const [sales, setSales] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    book_id: "",
    quantity: "1",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes, booksRes] = await Promise.all([
        axios.get(`${API_URL}/direct-sales`),
        axios.get(`${API_URL}/books`)
      ]);
      const sorted = salesRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setSales(sorted);
      setBooks(booksRes.data.filter(b => b.stock > 0));
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/direct-sales`, {
        book_id: formData.book_id,
        quantity: parseInt(formData.quantity),
        notes: formData.notes || null
      });
      toast.success("Venta registrada");
      setShowModal(false);
      setFormData({ book_id: "", quantity: "1", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar venta");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectedBook = books.find(b => b.id === formData.book_id);
  const totalPrice = selectedBook ? selectedBook.price * parseInt(formData.quantity || 0) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/libritos">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ventas Presenciales</h1>
            <p className="text-gray-500">Registrar ventas directas en mostrador</p>
          </div>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-violet-600 hover:bg-violet-700">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Venta
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Historial de Ventas ({sales.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Libro</TableHead>
                <TableHead>Cantidad</TableHead>
                <TableHead>Precio Unit.</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead>Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sales.map((sale) => (
                <TableRow key={sale.id}>
                  <TableCell className="font-mono text-sm">{sale.id}</TableCell>
                  <TableCell className="font-medium">{sale.book_title}</TableCell>
                  <TableCell>{sale.quantity}</TableCell>
                  <TableCell>${sale.unit_price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</TableCell>
                  <TableCell className="font-semibold">${sale.total_price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</TableCell>
                  <TableCell className="text-sm text-gray-500">{sale.notes || "-"}</TableCell>
                  <TableCell className="text-sm">{formatDate(sale.created_at)}</TableCell>
                </TableRow>
              ))}
              {sales.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No hay ventas registradas
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New Sale Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Nueva Venta Presencial
            </DialogTitle>
            <DialogDescription>
              Registrá una venta directa realizada en mostrador
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="book_id">Libro *</Label>
              <Select value={formData.book_id} onValueChange={(v) => setFormData({ ...formData, book_id: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar libro" />
                </SelectTrigger>
                <SelectContent>
                  {books.map(book => (
                    <SelectItem key={book.id} value={book.id}>
                      {book.title} (Stock: {book.stock}) - ${book.price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="quantity">Cantidad *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                max={selectedBook?.stock || 999}
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
                className="mt-1"
              />
              {selectedBook && (
                <p className="text-xs text-gray-500 mt-1">Stock disponible: {selectedBook.stock}</p>
              )}
            </div>
            <div>
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Nombre del cliente, forma de pago, etc."
                className="mt-1"
              />
            </div>
            
            {selectedBook && (
              <div className="bg-violet-50 rounded-lg p-4">
                <p className="text-sm text-violet-700">
                  <strong>Precio unitario:</strong> ${selectedBook.price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                </p>
                <p className="text-lg text-emerald-800 font-bold">
                  <strong>Total:</strong> ${totalPrice?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-violet-600 hover:bg-violet-700" disabled={!formData.book_id}>
                Registrar Venta
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
